import {
  useState,
  useMemo,
  useEffect,
  createContext,
  useContext as _useContext,
  Suspense,
  FC,
  useCallback,
} from 'react';

import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import {
  CompanyContactProfile,
  ContactAttachments,
  ContactContacts,
  ContactDeals,
  UploadAttachmentsModal,
} from 'modules/account/contact/components';
import { Error, Loading } from 'pages';
import {
  useGetCompanyContactDetails,
  useGetDeals,
} from 'modules/account/contact/hooks';
import { NoData } from 'ui/partials/PagePlaceholder/PagePlaceholder';
import { Accordion, Button } from 'ui/components/common';
import { AiOutlinePlus } from 'react-icons/ai';
import { cn } from 'utils/helpers';
import { Tabs } from 'ui/components/nav';
import { SIZE } from 'utils/constants';
import useModal from 'ui/partials/Modal/useModal';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import c from 'utils/constants';
import { useGetSubscription } from 'modules/account/settings/hooks';

import Activity from './Activity';
import Notes from './Notes';
import {
  UpdateContactDealModal,
  UpdateCompanyLeadModal,
} from '../../components';

const CompanyProfileContext = createContext<Record<string, any>>({});
// eslint-disable-next-line react-refresh/only-export-components
export const useCompanyProfileContext = () =>
  _useContext(CompanyProfileContext);

const TAB_ROUTES = [
  {
    to: '',
    element: (id: string) => <Activity id={id} />,
    index: true,
    label: 'Activity',
  },
  {
    to: 'notes',
    element: (id: string, source: string) => <Notes id={id} source={source} />,
    label: 'Notes',
  },
];

const tabs = TAB_ROUTES.map((tab) => ({
  value: tab.to,
  path: tab.to,
  label: tab.label,
}));

const SelectedCompany: FC = () => {
  const { data: subscription } = useGetSubscription();
  const { showModal } = useModal();
  const navigate = useNavigate();
  const params = useParams();
  const [, setIsFormEdit] = useState(false);

  const [data, setData] = useState<Record<string, any>>();
  const { isLoading, data: companyContactDetails } =
    useGetCompanyContactDetails(params.id as string, {
      include: ['leads', 'tag'],
    });

  const { data: deals } = useGetDeals({ companyId: params.id });

  const { ACTIONS, PERMISSION_KEYS } = c;
  const canEditDeal = useCanRolePermissions(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.DEALS,
  );
  const canEditLead = useCanRolePermissions(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.LEADS,
  );

  const handleShowUploadAttachmentsModal = () => {
    showModal({
      content: (
        <UploadAttachmentsModal
          referenceId={params.id as string}
          source="Company"
        />
      ),
      hideHeader: true,
      disableOutsideClick: true,
      size: SIZE.Large,
      title: '',
    });
  };

  useEffect(() => {
    setData({
      ...companyContactDetails,
      activities: [],
      calls: [],
      id: params.id,
    });
  }, [params.id, companyContactDetails]);

  const value = useMemo(
    () => ({
      data,
      isLoading,
    }),
    [data, isLoading],
  );

  const handleShowEditDeal = useCallback(() => {
    setIsFormEdit(false);
    showModal({
      title: 'Edit Deal',
      content: (
        <UpdateContactDealModal
          payload={{ companyId: params.id }}
          source="companies"
        />
      ),
      disableOutsideClick: true,
    });
  }, [showModal, params.id]);

  const handleShowEditLead = useCallback(() => {
    setIsFormEdit(false);
    showModal({
      title: 'Edit Contact',
      content: <UpdateCompanyLeadModal id={params.id ?? ''} />,
      disableOutsideClick: true,
    });
  }, [showModal, params.id]);

  if (!isLoading && !companyContactDetails) return <NoData />;

  return (
    <div className="flex layout-gap items-start">
      <CompanyProfileContext.Provider value={value}>
        <CompanyContactProfile />
        <div
          className={cn(
            'w-full min-w-[488px]',
            'px-[22px] pt-[7px]',
            'bg-fill-tertiary rounded-[6px] shadow-leapify-sm',
          )}
        >
          <Tabs
            selectedTab={location.pathname.split(`${params.id!}/`)[1] ?? ''}
            onChange={(val) => navigate(val.value!)}
            size={SIZE.Small}
            type="secondary"
            className="w-full"
            items={tabs}
          />
          <div className={cn('h-[0.5px] w-full bg-separator-primary mb-5')} />
          <Suspense fallback={<Loading />}>
            <Routes>
              {TAB_ROUTES.map(({ to, element, index }) => (
                <Route
                  key={Math.random()}
                  path={to}
                  index={Boolean(index)}
                  element={element(params.id!, 'Company')}
                />
              ))}
              <Route path="*" element={<Error />} />
            </Routes>
          </Suspense>
        </div>
        <div className="flex flex-col layout-gap min-w-[280px] max-w-[280px]">
          <div
            className={cn(
              'rounded-[6px] pt-[15px] p-[22px]',
              'w-full bg-fill-tertiary shadow-leapify-sm',
            )}
          >
            <Accordion
              label={`Contacts ${
                !companyContactDetails?.meta?.total
                  ? ''
                  : `(${companyContactDetails?.meta?.total})`
              }`}
              arrowLeft
              defaultOpen
              content={<ContactContacts id={params.id ?? ''} />}
              action={
                <Button
                  className="font-semibold text-secondary p-0"
                  icon={AiOutlinePlus}
                  type="button"
                  label="ADD"
                  onClick={handleShowEditLead}
                  left
                  intent="text"
                  disabled={subscription?.isExpired}
                  expiredSubscription={subscription?.isExpired}
                />
              }
            />
          </div>
          <div
            className={cn(
              'rounded-[6px] pt-[15px] p-[22px]',
              'w-full bg-fill-tertiary shadow-leapify-sm',
            )}
          >
            <Accordion
              label="Attachments"
              arrowLeft
              defaultOpen
              content={<ContactAttachments source="Company" />}
              action={
                <Button
                  className="font-semibold text-secondary p-0"
                  icon={AiOutlinePlus}
                  type="button"
                  intent="text"
                  label="ADD"
                  onClick={handleShowUploadAttachmentsModal}
                  disabled={!canEditLead || subscription?.isExpired}
                  expiredSubscription={subscription?.isExpired}
                  left
                />
              }
            />
          </div>
          <div
            className={cn(
              'rounded-[6px] pt-[15px] p-[22px]',
              'w-full bg-fill-tertiary shadow-leapify-sm',
            )}
          >
            <Accordion
              label={`Deals ${!deals?.meta?.total ? '' : `(${deals?.meta?.total})`}`}
              arrowLeft
              defaultOpen
              content={
                <ContactDeals payload={{ companyId: params.id ?? '' }} />
              }
              action={
                <Button
                  className="font-semibold text-secondary p-0"
                  icon={AiOutlinePlus}
                  type="button"
                  label="ADD"
                  intent="text"
                  onClick={handleShowEditDeal}
                  disabled={!canEditDeal || subscription?.isExpired}
                  expiredSubscription={subscription?.isExpired}
                  left
                />
              }
            />
          </div>
        </div>
      </CompanyProfileContext.Provider>
    </div>
  );
};

export default SelectedCompany;
